import { FormProps } from '@deltek/specification-client/core/Models/formProps'
import { ManufacturerProductWebLinkDetail } from '@deltek/specification-client/core/Models/product'
import { ListingDetailLinkInput } from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { reorderArrayItems } from '@deltek/specification-client/core/Utilities/formUtils'
import { validateFullUrlPath } from '@deltek/specification-client/core/Utilities/urlUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React from 'react'
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd'
import { BlueActionButton } from '../BlueActionButton'
import { EditLinkRow } from './EditLinkRow'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        maxWidth: 544,
    },
    fullWidth: {
        width: '100%',
    },
    title: {
        color: '#04325B',
        fontWeight: 500,
        borderBottom: '6px solid #04325B',
        textTransform: 'uppercase',
    },
    itemContainer: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    button: {
        '& button': {
            height: 40,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
    },
}))

export interface NormalizedListingDetailLinkInput {
    id: number
    title: FormProps
    url: FormProps
    new?: boolean
}

interface EditLinksProps {
    pastLinks?: Partial<ManufacturerProductWebLinkDetail>[]
    handleWebLinksChange: (
        links: Partial<ListingDetailLinkInput>[],
        valid: boolean
    ) => void
}

const defaultLink = {
    title: { value: '', valid: true, required: true },
    url: { value: '', valid: true, required: true },
    id: 0,
} as NormalizedListingDetailLinkInput

export const EditLinks: React.FC<EditLinksProps> = ({
    pastLinks,
    handleWebLinksChange,
}) => {
    const classes = useStyles()

    const [localLinks, setLocalLinks] = React.useState<
        NormalizedListingDetailLinkInput[]
    >(
        pastLinks && pastLinks?.length > 0
            ? pastLinks?.map((each, index) => ({
                  url: {
                      value:
                          each.url === 'http://' || each.url === 'https://'
                              ? ''
                              : each.url ?? '',
                      valid: true,
                      required: true,
                  },
                  title: {
                      value: each.type ?? '',
                      valid: true,
                      required: true,
                  },
                  id: index,
              }))
            : [defaultLink]
    )

    const disabled =
        localLinks.length === 1
            ? localLinks[0].title.value === '' ||
              localLinks[0].url.value === '' ||
              (!!localLinks[0].url.value &&
                  !validateFullUrlPath(localLinks[0].url.value ?? ''))
            : localLinks.some((each) => !each.url.valid || !each.title.valid)

    const handleAddNewLink = () => {
        const local = [
            ...localLinks,
            {
                id: localLinks.length,
                url: { value: '', valid: false, required: true },
                title: { value: '', valid: false, required: true },
                new: true,
            },
        ]
        setLocalLinks(local)

        handleWebLinksChange(
            local.map((each) => ({
                url: each.url.value ?? '',
                type: each.title.value ?? '',
                order: each.id,
            })),
            !local.some((each) => !each.url.valid || !each.title.valid)
        )
    }

    const handleUpdateLink = (detail: NormalizedListingDetailLinkInput) => {
        const local = localLinks.map((each) =>
            each.id === detail.id
                ? {
                      ...detail,
                      new: false,
                  }
                : each
        )
        setLocalLinks(local)

        handleWebLinksChange(
            local.map((each) => ({
                url: each.url.value ?? '',
                type: each.title.value ?? '',
                order: each.id,
            })),
            !local.some((each) => !each.url.valid || !each.title.valid)
        )
    }

    const handleDeleteLink = (id: number) => {
        const local =
            localLinks.length === 1
                ? [defaultLink]
                : (localLinks
                      .filter((each) => each.id !== id)
                      .map((each, index) => ({
                          ...each,
                          id: index,
                      })) as NormalizedListingDetailLinkInput[])

        setLocalLinks(local)

        handleWebLinksChange(
            local.map((each) => ({
                url: each.url.value ?? '',
                type: each.title.value ?? '',
                order: each.id,
            })),
            !local.some((each) => !each.url.valid || !each.title.valid)
        )
    }

    const onSortEnd = (result: DropResult) => {
        if (!result.destination) {
            return
        }
        const local = reorderArrayItems(
            localLinks,
            result.source.index,
            result.destination.index
        )
        setLocalLinks(local)

        handleWebLinksChange(
            local.map((each) => ({
                url: each.url.value ?? '',
                type: each.title.value ?? '',
                order: each.id,
            })),
            !local.some((each) => !each.url.valid || !each.title.valid)
        )
    }

    return (
        <GridContainer data-testid="dvLinks" className={classes.container}>
            <GridContainer justifyContent="center" alignItems="center">
                <GridItem xs={1} />
                <GridItem xs={11}>
                    <Typography variant="h6" className={classes.title}>
                        Links
                    </Typography>
                </GridItem>
            </GridContainer>
            <GridContainer
                item
                className={clsx(classes.itemContainer, classes.fullWidth)}
                direction="column"
            >
                <DragDropContext onDragEnd={onSortEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <GridItem
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {localLinks?.map(
                                    (
                                        each: NormalizedListingDetailLinkInput,
                                        index: number
                                    ) => (
                                        <EditLinkRow
                                            key={each.id}
                                            index={index}
                                            detail={each}
                                            last={
                                                index === localLinks.length - 1
                                            }
                                            onlyOne={localLinks?.length === 1}
                                            handleUpdateLink={handleUpdateLink}
                                            handleDeleteLink={handleDeleteLink}
                                        />
                                    )
                                )}
                                {provided.placeholder}
                            </GridItem>
                        )}
                    </Droppable>
                </DragDropContext>
                <GridContainer item>
                    <GridItem xs={1} />
                    <GridItem xs={11}>
                        <BlueActionButton
                            additionalClassName="dvButton"
                            actionButtonProps={{
                                onClick: handleAddNewLink,
                                disabled,
                            }}
                        >
                            Add New Link
                        </BlueActionButton>
                    </GridItem>
                </GridContainer>
            </GridContainer>
        </GridContainer>
    )
}
